import {
  BankAccountDetails as BankAccountDetailsProto,
  BankAccountType as BankAccountTypeProto,
  CheckTaxRefundDeliveryMethod as CheckTaxRefundDeliveryMethodProto,
  DirectDepositTaxRefundDeliveryMethod as DirectDepositTaxRefundDeliveryMethodProto,
  FutureTaxCreditTaxRefundDeliveryMethod as FutureTaxCreditTaxRefundDeliveryMethodProto,
  TaxRefundDeliveryMethod as TaxRefundDeliveryMethodProto,
  type TaxRefundMethod as TaxRefundMethodProto,
  TaxRefundType as TaxRefundTypeProto,
} from '@taxfyle/api-internal/internal/tax_refund_methods_pb'
import { Id } from '@/infra/id'
import { safeTimestampToDate } from '@/utils/grpc-util'
import {
  mapPhysicalAddressProtoToProps,
  mapPhysicalAddressToProto,
} from '@/features/jobs/info-gathering/physical-address-mappers'
import { BankAccountDetails, type BankAccountType } from './BankAccount'
import type { TaxRefundDeliveryMethod } from './TaxRefundDeliveryMethod'
import type {
  TaxRefundMethodVersionProps,
  TaxRefundType,
} from './TaxRefundMethodVersion'

export function mapTaxRefundMethodProtoToProps(
  proto: TaxRefundMethodProto,
): TaxRefundMethodVersionProps {
  const deliveryMethodProto = proto.getDeliveryMethod()
  return {
    id: Id(proto.getId()),
    version: proto.getVersion(),
    refundType: mapTaxRefundTypeProtoToProps(proto.getRefundType()),
    deliveryMethod: deliveryMethodProto
      ? mapTaxRefundDeliveryMethodProtoToProps(deliveryMethodProto)
      : null,
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
  }
}

export function mapTaxRefundDeliveryMethodProtoToProps(
  proto: TaxRefundDeliveryMethodProto,
): TaxRefundDeliveryMethod | null {
  switch (proto.getTypeCase()) {
    case TaxRefundDeliveryMethodProto.TypeCase.CHECK: {
      const mailingAddressProto = proto.getCheck()?.getMailingAddress()
      return {
        type: 'Check',
        mailingAddress: mailingAddressProto
          ? mapPhysicalAddressProtoToProps(mailingAddressProto)
          : null,
      }
    }
    case TaxRefundDeliveryMethodProto.TypeCase.DIRECT_DEPOSIT: {
      const bankAccountProto = proto.getDirectDeposit()?.getBankAccount()
      return {
        type: 'DirectDeposit',
        bankAccount: bankAccountProto
          ? mapBankAccountDetailsProtoToProps(bankAccountProto)
          : BankAccountDetails.empty,
      }
    }
    case TaxRefundDeliveryMethodProto.TypeCase.FUTURE_TAX_CREDIT:
      return { type: 'FutureTaxCredit' }
    case TaxRefundDeliveryMethodProto.TypeCase.TYPE_NOT_SET:
      return null
  }
}

export function mapBankAccountDetailsProtoToProps(
  proto: BankAccountDetailsProto,
): BankAccountDetails {
  return {
    accountType: mapBankAccountTypeProtoToProps(proto.getAccountType()),
    accountNumber: proto.getAccountNumber(),
    routingNumber: proto.getRoutingNumber(),
    bankName: proto.getBankName(),
  }
}

export function mapTaxRefundTypeProtoToProps(
  proto: TaxRefundTypeProto,
): TaxRefundType {
  switch (proto) {
    case TaxRefundTypeProto.PERSONAL:
      return 'Personal'
    case TaxRefundTypeProto.BUSINESS:
      return 'Business'
    default:
      return 'Unspecified'
  }
}

export function mapBankAccountTypeProtoToProps(
  proto: BankAccountTypeProto,
): BankAccountType {
  switch (proto) {
    case BankAccountTypeProto.CHECKING:
      return 'Checking'
    case BankAccountTypeProto.SAVINGS:
      return 'Savings'
    default:
      return 'Unspecified'
  }
}

export function mapTaxRefundTypeToProto(
  type: TaxRefundType,
): TaxRefundTypeProto {
  switch (type) {
    case 'Personal':
      return TaxRefundTypeProto.PERSONAL
    case 'Business':
      return TaxRefundTypeProto.BUSINESS
    case 'Unspecified':
      return TaxRefundTypeProto.TAX_REFUND_TYPE_UNSPECIFIED
  }
}

export function mapTaxRefundDeliveryMethodToProto(
  method: TaxRefundDeliveryMethod,
): TaxRefundDeliveryMethodProto {
  const proto = new TaxRefundDeliveryMethodProto()
  switch (method.type) {
    case 'Check': {
      const check = new CheckTaxRefundDeliveryMethodProto()
      if (method.mailingAddress) {
        check.setMailingAddress(
          mapPhysicalAddressToProto(method.mailingAddress),
        )
      }
      return proto.setCheck(check)
    }
    case 'DirectDeposit':
      return proto.setDirectDeposit(
        new DirectDepositTaxRefundDeliveryMethodProto().setBankAccount(
          mapBankAccountDetailsToProto(method.bankAccount),
        ),
      )
    case 'FutureTaxCredit':
      return proto.setFutureTaxCredit(
        new FutureTaxCreditTaxRefundDeliveryMethodProto(),
      )
  }
}

export function mapBankAccountDetailsToProto(
  details: BankAccountDetails,
): BankAccountDetailsProto {
  return new BankAccountDetailsProto()
    .setAccountType(mapBankAccountTypeToProto(details.accountType))
    .setAccountNumber(details.accountNumber)
    .setRoutingNumber(details.routingNumber)
    .setBankName(details.bankName)
}

export function mapBankAccountTypeToProto(
  type: BankAccountType,
): BankAccountTypeProto {
  switch (type) {
    case 'Checking':
      return BankAccountTypeProto.CHECKING
    case 'Savings':
      return BankAccountTypeProto.SAVINGS
    case 'Unspecified':
      return BankAccountTypeProto.BANK_ACCOUNT_TYPE_UNSPECIFIED
  }
}
